// @flow

/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import getConfigApps from '../config';
import Url from '../../src/utils/Url';
import platform from 'platform';
import DeviceDetectionUtil from './DeviceDetectionUtil';
import DownloadUtil from './DownloadUtil';
import {
	appCodeToJILWebAppName,
	FFCLocaleMap,
	customerSegmentValues,
	pathTitleMap,
	starterTab,
	productIdToSapCode,
	productIdMap,
	CCWebRevampCategoryList,
	subCategoryList,
	sapCodeToProductMetaData,
	stockUrlParams,
	companionMobileAppMap,
	singleAppPlans,
	nujM3SingleAppPlansJDI
} from '../constants/appsConstants';
import {
	ALL_APPS,
	CCPP_PLAN,
	CCPPN_PLAN,
	ALL_APPS_PLAN,
	SUBSTANCE_COLLECTION_PLAN,
	SUBSTANCE_TEXTURING_PLAN
} from '../constants/AppPicker/appPickerConstants';
import {ccHomeCommands, wamMsgStatusType} from '../constants/wamConstants';
import {productData} from '../constants/ccdConstants';
import {setCookie, getCookie} from './cookie';
import CompatibilityUtil from './CompatibilityUtil';
import {WebStorage} from 'cchome-shared/utils';

class AppsUtil {
	isappsFFCProductListLoaded: boolean;
	appsFFCProductList: Object;
	appsFFCServiceList: Object;
	appsFFCMobileList: Object;
	ffcFallbackProductsJson: Object;
	allVersionsFFCProductList: Object;
	hasThor: ?boolean;
	isStarterPlanRedirect: boolean;
	intl: Object;
	isAppLineageMapInit: ?boolean;
	sophiaAnalyticsData: Object;
	appLineageMap: Object;
	CCXUIAppStore: Object;
	informationDialogStore: Object;
	CCXUIPDPStore: Object;
	isCCXView: ?boolean;
	isFFCFallback: ?boolean;
	starterRedirectPlanCode: ?boolean;
	isAutoNonStarterInstallation: ?boolean;
	sophiaPpdEndPoint: ?Object;
	disableAutoDownload: Function;
	hideCCInstallationToast: Function;
	hideCCInstalationBanner: Function;
	planCode: ?string;
	planStatus: ?string;
	customerSegment: ?Object;
	sessionStorageGet: Function;
	localStorageGet: Function;
	localStorageSet: Function;
	profile: ?Object;
	addMiniPlanModal: Function;
	locale: ?string;
	ccInstallationToastXButtonState: ?string;
	banner: Object;
	productsData: ?Object;
	cdnInfo: ?Object;
	entitlementData: ?Object;
	pageStartTime: ?number;
	shouldRenderAppPickerPhotoPlan: ?Function;
	sessionStorageSet: Function;
	jarvisInitiated: Promise<any>;
	static imsData;
	static visitorGuid;

	constructor() {
		this.planStatus = null;
		this.customerSegment = null;
		this.isappsFFCProductListLoaded = false;
		this.appsFFCProductList = null;
		this.appsFFCServiceList = null;
		this.appsFFCMobileList = null;
		this.allVersionsFFCProductList = {};
		this.entitlementData = null;
		this.cdnInfo = null;
		this.productsData = null;
		this.locale = null;
		this.hasThor = null;
		this.starterRedirectPlanCode = null;
		this.isAutoNonStarterInstallation = null;
		this.isStarterPlanRedirect = false;
		this.installerPopUpTimer = null;
		this.hideCCInstallationToast = null;
		this.hideCCInstalationBanner = null;
		this.ccInstallationToastXButtonState = null;

		this.intl = {
			formatMessage: () => {}
		};
		this.isAppLineageMapInit = false;
		this.sophiaAnalyticsData = {};
		this.appLineageMap = {};
		// remove if store is returning the req object in callbacks.
		this.CCXUIAppStore = {};
		this.informationDialogStore = {};
		this.CCXUIPDPStore = {};
		this.sessionStorageUtil = new WebStorage('sessionStorage');
		this.sessionStorageSet = function(...args) {
			const domain = getConfigApps('COOKIE_DOMAIN');
			const secure = getConfigApps('COOKIE_SECURE');
			this.sessionStorageUtil.set(...args, domain, secure);
		};
		this.sessionStorageRemove = function(key) {
			const domain = getConfigApps('COOKIE_DOMAIN');
			const secure = getConfigApps('COOKIE_SECURE');
			this.sessionStorageUtil.remove(key, domain, secure);
		};
		this.sessionStorageGet = function(key) {
			return this.sessionStorageUtil.get(key);
		};


		this.localStorageSet = function(key, value) {
			try {
				localStorage.setItem(key, value);
			}
			catch (e) {
				setCookie(key, encodeURIComponent(value));
			}
		};

		this.localStorageGet = function(key) {
			try {
				return localStorage.getItem(key);
			}
			catch (e) {
				const value = getCookie(key);
				return value === null ? null : decodeURIComponent(value);
			}
		};

		this.jarvisInitiated = new Promise((resolve) => {
			this.jarvisPromiseResolve = resolve;
		});
		this.addMiniPlanModal = () => {};
		this.disableAutoDownload = () => {};
	}

	/**
	 * Initializes Apps Util
	 *
	 * @param appsFFCProductList Required param.
	 * @param entitlementData Required param.
	 * @param cdnInfo Required param.
	 * @param productsData Required param.
	 * @param locale Required param.
	 */
	init(appsFFCProductList: ?Object, entitlementData: ?Object, cdnInfo: ?Object, productsData: ?Object, locale: ?string) {
		/* In case a download for an app occurs before init for apps util was called,
		appsFFCProductList will only contain data for that specifc app, need to init appsFFCProductList again */
		if (this.appsFFCProductList === null || !this.isappsFFCProductListLoaded) {
			this.appsFFCProductList = appsFFCProductList;
			this.isappsFFCProductListLoaded = appsFFCProductList != null;
		}
		if (this.entitlementData === null) {
			this.entitlementData = entitlementData;
		}
		if (this.cdnInfo === null) {
			this.cdnInfo = cdnInfo;
		}
		if (this.productsData === null) {
			this.productsData = productsData;
		}
		if (this.locale === null) {
			this.locale = locale;
		}
	}

	/**
	 * Initializes Apps Util locale
	 * @param locale Required param.
	 */
	initLocale(locale?: string) {
		if (this.locale === null) {
			this.locale = locale;
		}
	}

	get isLoggedOutPromotionExp() {
		return this.getLoggedOutWorkflow() === 'promotion';
	}

	get isLoggedOutCampaignExp() {
		return this.getLoggedOutWorkflow() === 'campaign';
	}

	setCCInstallationToastXButtonState(value) {
		this.ccInstallationToastXButtonState = value;
	}

	getCCInstallationToastXButtonState() {
		return this.ccInstallationToastXButtonState;
	}

	openExternalLink(inputUrl, sapCode, guid, source, branchURLTag, target) {
		const urlArr = inputUrl.split('#');
		const url = urlArr[0];
		const connector = url.indexOf('?') > -1 ? '&' : '?';
		let extUrl = branchURLTag ? `${url}${connector}${branchURLTag}` : `${url}${connector}x-product=CCHome&x-product-location=Apps&guid=${guid}`;
		if (sapCode === 'stock-web' || sapCode === 'FFC_stock' || url.indexOf('stock') !== -1) {
			extUrl = this.addParamsToStockUrl(extUrl, source);
		}
		if (urlArr[1]) {
			extUrl = `${extUrl}#${urlArr[1]}`;
		}
		window.open(extUrl, target ? target : '_blank');
		return extUrl;
	}

	/**
	 * [setImsData sets the Ims data]
	 * @returns {[none]}
	 */
	setImsData(imsData) {
		this.imsData = imsData;
	}

	/**
	 * [getImsData returns the Ims data]
	 * @returns imsData
	 */
	getImsData() {
		return this.imsData;
	}

	/**
	 * [setLocale sets the locale data]
	 * @returns {[none]}
	 */
	setLocale(locale) {
		this.locale = locale;
	}

	/**
	 * [setVisitorGuid sets the Ims data]
	 * @returns {[none]}
	 */
	setVisitorGuid(visitorGuid) {
		this.visitorGuid = visitorGuid;
	}

	/**
	 * [getVisitorGuid returns the Ims data]
	 * @returns visitorGuid
	 */
	getVisitorGuid() {
		return this.visitorGuid;
	}

	getProductIcon(appObj) {
		let icons;
		if (!appObj) {
			return '';
		}
		if (appObj.mobileAppIcons) {
			icons = appObj.mobileAppIcons.icon;
		}
		else if (appObj.serviceIcons) {
			icons = appObj.serviceIcons.icon;
		}
		else if (appObj.productIcons) {
			icons = appObj.productIcons.icon;
		}
		let iconObj = icons && icons.find(elm => elm.size === '192x192');
		if (!(iconObj && iconObj.value)) {
			iconObj = icons && icons.find(elm => elm.size === '96x96');
		}
		return iconObj ? iconObj.value : '';
	}

	getProductDisplayName(productId) {
		const productConfig = this.getFFCProductConfig(productId);
		if (productConfig) {
			return productConfig.displayName;
		}
		else if (this.isLoggedOutExp() && productData[productId]) {
			return productData[productId].name;
		}
		return '';
	}

	isCCPPPlanCode(planCode: ?string) {
		switch (planCode) {
			case 'phlt_direct_individual':
			case 'ples_1tb_direct_individual':
			case 'ples_2tb_direct_individual':
			case 'ples_5tb_direct_individual':
			case 'ples_10tb_direct_individual':
			case 'ppsk_direct_individual':
				return true;

			default:
				return false;
		}
	}

	isAllAppsPlanCode(planCode: string) {
		switch (planCode) {
			case 'ccsn_direct_individual':
			case 'cisk_direct_individual':
			case 'creative_cloud_all_apps_with_1tb_individual':
			case 'creative_cloud_all_apps_with_2tb_cloud_services_individual':
			case 'creative_cloud_all_apps_with_5tb_cloud_services_individual':
			case 'creative_cloud_all_apps_with_10_tb_cloud_services_individual':
			case 'ccsv_enterprise_ml':
			case 'ccle_indirect_enterprise_ml':
			case 'ccsv_k12_enterprise_ml_edu_email_k12':
			case 'ccle_indirect_enterprise_ml_edu_email_hed':
			case 'ccle_direct_indirect_team':
			case 'ctsk_direct_indirect_team':
				return true;
		}
		return false;
	}

	getProductTagLine(productObj,locale) {
		let languageDescriptionMapArray;
		if (productObj.type === 'Service') {
			if (productObj.serviceTagline && productObj.serviceTagline.tagLine) {
				languageDescriptionMapArray = productObj.serviceTagline.tagLine;
			}
		}
		else if (productObj.type === 'Mobile App') {
			if (productObj.mobileAppTagline && productObj.mobileAppTagline.tagLine) {
				languageDescriptionMapArray = productObj.mobileAppTagline.tagLine;
			}
		}
		else {
			const customEntryArray = productObj['custom-data']['custom-entry'];
			const customEntry = customEntryArray ? customEntryArray.find(c => c.key === 'productDescriptionTagline') : null;
			languageDescriptionMapArray = customEntry ? JSON.parse(customEntry.value['0']).Language : null;
		}
		const ffcString = languageDescriptionMapArray ? languageDescriptionMapArray.find(m => m.locale === FFCLocaleMap[locale]) : null;
		if (ffcString && ffcString.value) {
			return ffcString.value;
		}
		return '';
	}

	sortFFCProductArray(productArr) {
		return productArr.sort((a, b) =>
			this.getProductSortIndex(a) - this.getProductSortIndex(b)
		);
	}

	getProductSortIndex(product) {
		switch (product.id) {
			case 'KCCC':
				return 0;
			default:
				return product.sortIndex;
		}
	}

	isProductAFreeProduct = (sapCode: string) => {
		if (this.appsFFCProductList[sapCode]) {
			const product = this.appsFFCProductList[sapCode];
			let platformObject = this.getPlatformObject(product.platforms.platform);

			// If we can't determine the platform, use the first platform in the list.
			platformObject = platformObject ? platformObject : product.platforms.platform[0];

			if (platformObject) {
				const userLocale = FFCLocaleMap.en || 'en_US';
				// as per CCPD code, use URLs for en_US by default
				let languageSet = this.getLanguageSet(platformObject, userLocale);
				languageSet = languageSet ? languageSet : platformObject.languageSet[0];
				const customData = languageSet['custom-data'];
				if (customData && customData['custom-entry'].length > 0) {
					for (let i = 0; i < customData['custom-entry'].length; i++) {
						if (customData['custom-entry'][i].key === 'isFreeProduct') {
							if (customData['custom-entry'][i].value[0] === 'true') {
								return true;
							}
							break;
						}
					}
				}
			}
		}
		return false;
	};

	/**
	 * [getPathData grabs the relveant apps fields from the url]
	 * @returns {[none]}
	 */

	getPathData() {
		const path = window.location.pathname.split('/');
		const supportedLanguages = Object.keys(getConfigApps('SUPPORTED_LANGUAGES'));
		if (supportedLanguages.indexOf(path[1]) > -1) {
			return {
				mainAppsTab : path[3],
				product: path[4] ? path[4].toLowerCase() : path[4],
				pdpTab: path[5]
			};
		}
		return {
			mainAppsTab : path[2],
			product: path[3] ? path[3].toLowerCase() : path[3],
			pdpTab: path[4]
		};
	}

	getProductIdFromUrl(path: Array<string>) {
		return path[5] ? path[5].toLowerCase() : Url.getParam('productId');
	}

	/**
	 * Function to return the product id in the url passed as parameter
	 * @param url: Required parameter from which product Id needs to be extracted
	 * @return: productId
	 *
	 */
	getProductId(url: Array<string>) {
		const path = url.split('/');
		if (path[4] === 'pdp') {
			return path[5];
		}
		if (path[4] === 'installation') {
			return path[3];
		}
		if (path[2] === 'starter') {
			return path[3];
		}
		if (Url.getParam('productId', url)) {
			return Url.getParam('productId', url);
		}
		if (path.length === 4 && path[1] === 'apps' && CCWebRevampCategoryList.includes(path[2]) && !subCategoryList.includes(path[3])) {
			return path[3];
		}
		if (path[2] === 'download') {
			return path[3];
		}
		return '';
	}

	/**
	 * Function to return the index(location) of product id in the url passed as parameter
	 * @param url: Required parameter from which index(location) of product Id needs to be calculated
	 * @return: integer as location of productId
	 *
	 */
	getIndexOfProductId(url: Array<string>) {
		const path = url.split('/');
		if (path[4] === 'pdp') {
			return 5;
		}
		if (path[4] === 'installation') {
			return 3;
		}
		if (path[2] === 'starter') {
			return 3;
		}
		if (path.length === 4 && path[1] === 'apps' && CCWebRevampCategoryList.includes(path[2]) && !subCategoryList.includes(path[3])) {
			return 3;
		}
		if (path[2] === 'download') {
			return 3;
		}
	}

	/**
	 * [shouldRedirectToNewProductId tells whether the redirection should happen or not]
	 * @returns: boolean - whether the redirection should happen or not
	 */
	shouldRedirectToNewProductId() {
		const productId = this.getProductId(window.location.pathname);
		if (productId && Object.keys(productIdMap).includes(productId)) {
			// productId present in redirection map
			const featureFlagPresent = productIdMap[productId].featureFlagPresent;
			const featureFlagAbsent = productIdMap[productId].featureFlagAbsent;
			if (featureFlagPresent) {
				// if ff present that enables this redirection, check for its presence
				return this.hasFeature(featureFlagPresent);
			}
			else if (featureFlagAbsent) {
				// if ff present that disables this redirection, check for its abscence
				return !this.hasFeature(featureFlagAbsent);
			}
			return true;
		}
	}


	/**
	 * Function to redirect ProductId url to new(changed) ProductId url
	 */
	redirectToNewProductId() {
		if (!this.shouldRedirectToNewProductId()) {
			return;
		}
		const url = window.location.pathname;
		const productId = this.getProductId(url);
		let newUrl = url;
		const newProductId = productIdMap[productId].newProductId;
		if (Url.getParam('productId', url)) {
			newUrl = Url.replaceParamInUrl('productId', newProductId, newUrl);
			window.location.pathname = newUrl;
		}
		else {
			const path = url.split('/');
			const indexOfProdId = this.getIndexOfProductId(url);
			newUrl = url.replace(path[indexOfProdId], newProductId);
			window.location.pathname = newUrl;
		}
	}

	/**
	 * [getCCWebPathData grabs the relveant App fields from the url]
	 * @returns {[none]}
	 */

	getCCWebPathData(pathname) {
		const path = (pathname && pathname.split('/')) || window.location.pathname.split('/');
		return {
			sectionId : path[2],
			subSectionId: path[3],
			isPDP: path[4] && path[4].toLowerCase() === 'pdp',
			product: this.getProductIdFromUrl(path)
		};
	}

	isSearchEngineResult() {
		return DownloadUtil.checkInputUrlValidity(document.referrer, ['duckduckgo.com', 'google.com', 'bing.com']);
	}

	/**
	 * [getLoggedOutExpPathData grabs the relveant apps fields from the url]
	 * @returns {[none]}
	 */

	getLoggedOutExpPathData() {
		const path = window.location.pathname.split('/');
		const supportedLanguages = Object.keys(getConfigApps('SUPPORTED_LANGUAGES'));
		if (supportedLanguages.indexOf(path[1]) > -1) {
			return {
				mainAppsTab : path[3],
				product: path[4],
				workflow: path[5]
			};
		}
		return {
			mainAppsTab : path[2],
			product: path[3],
			workflow: path[4]
		};
	}

	getLoggedOutWorkflow() {
		const pathData = this.getLoggedOutExpPathData();
		return pathData.workflow;
	}

	isLoggedOutExp() {
		return this.getLoggedOutExpPathData().mainAppsTab === 'download';
	}

	getFFCProductConfig(productId) {
		return this.getFFCProductConfigBySapCode(this.getSapCodeFromProductId(productId));
	}

	getFFCProductConfigBySapCode(sapCode) {
		let productConfig = this.appsFFCProductList && this.appsFFCProductList[sapCode];
		if (!productConfig) {
			productConfig = this.appsFFCServiceList && this.appsFFCServiceList[sapCode];
		}
		if (!productConfig) {
			productConfig = this.appsFFCMobileList && this.appsFFCMobileList[sapCode];
		}
		return productConfig;
	}

	// get app lineage from desktop sap code
	getAppLineageFromSapCode(sapCode) {
		return this.getFFCProductConfigBySapCode(sapCode).appLineage;
	}

	/**
	 * Returns the sap code corresponding to the given product id based on the workflow (logged-out page or logged-in page).
	 * @param {string} productId Id of the product
	 * @return {string} corresponding sap code
	 */
	getSapCodeFromProductId(productId: ?string) {
		if (this.isLoggedOutExp()) {
			return productData[productId].sapCode;
		}
		return productIdToSapCode[productId];
	}

	getProductPlatform(ffcData) {
		switch (ffcData.type) {
			case 'Mobile App' :
				return 'mobile';
			case 'Service' :
				return 'web';
			default :
				return ffcData.type.toLowerCase();
		}
	}

	getSubCategory(category, sapCode) {
		if (category === 'all') {
			if (sapCode && this.getFFCProductConfigBySapCode(sapCode)) {
				return this.getProductPlatform(this.getFFCProductConfigBySapCode(sapCode));
			}
			return 'desktop';
		}
		return 'all';
	}

	getCCWebURL(sapCode: ?string, platformType: ?string, startInstall: ?boolean, isPostPurchaseInstall: ?boolean, source: ?string) {
		const routeParam = this.getCCWebPathData();
		let sectionId = routeParam.sectionId ? routeParam.sectionId : 'all';
		let subSectionId = routeParam.subSectionId ? routeParam.subSectionId : 'all';
		let productid = '';
		let action = '';
		if (sectionId === 'starter' && [ALL_APPS,CCPP_PLAN, CCPPN_PLAN, ALL_APPS_PLAN, SUBSTANCE_COLLECTION_PLAN, SUBSTANCE_TEXTURING_PLAN].includes(subSectionId)) {
			// This is the case for all apps starter page - NUJ
			sectionId = 'all';
			subSectionId = 'all';
		}
		if (sectionId === 'all' && subSectionId === 'all') {
			subSectionId = platformType;
		}

		action = startInstall ? '?action=install' : '';
		if (isPostPurchaseInstall) {
			action = (action === '') ? '?isStarter=true' : `${action}&isStarter=true`;
		}
		if (source) {
			action = (action === '') ? `?source=${source}` : `${action}&source=${source}`;
		}

		if (sapCode) {
			productid = `/pdp/${this.getProductIdFromSapcode(sapCode)}`;
			const productConfig = this.getFFCProductConfigBySapCode(sapCode);
			document.title = productConfig.displayName + this.intl.formatMessage({id: 'apps.document.title.suffix'});
		}
		return (`/apps/${sectionId}/${subSectionId}${productid}${action}`);
	}

	getInlineErrorLabel(command, appStatus) {
		if (appStatus === wamMsgStatusType.appUnknown) {
			return 'apps.inline.status.appunknown';
		}
		switch (command) {
			case ccHomeCommands.ccHomeToThorCommands.install:
				return 'apps.inline.install.error';
			case ccHomeCommands.ccHomeToThorCommands.uninstall:
				return 'apps.inline.uninstall.error';
			case ccHomeCommands.ccHomeToThorCommands.update:
				return 'apps.inline.update.error';
			default:
				return 'apps.inline.default.error';
		}
	}

	getCCDGoURL(locale, base) {
		let ret = base;
		if (locale === 'sv') {
			ret = `${base}_se`;
		}
		else if (locale === 'nb') {
			ret = `${base}_no`;
		}
		else if (locale === 'ko') {
			ret = `${base}_kr`;
		}
		else if (locale === 'cs') {
			ret = `${base}_cz`;
		}
		else if (locale === 'da') {
			ret = `${base}_dk`;
		}
		else if (locale === 'pt') {
			ret = `${base}_br`;
		}
		else if (locale === 'ja') {
			ret = `${base}_jp`;
		}
		else if (locale === 'zh-Hant') {
			ret = `${base}_tw`;
		}
		else if (locale === 'zh-Hans') {
			ret = `${base}_cn`;
		}
		else if (locale !== 'en' && locale !== 'en-us') {
			ret = `${base}_${locale}`;
		}
		return ret;
	}

	getCCHostName() {
		return getConfigApps('servers').CC;
	}

	/**
	 * [routeConstructor takes in the route schema passed in from host app and returns route with the appropriate fields. If there is nothing applicable for a param, put empty string ''.]
	 * @param  {[strings]} path
	 * @param  {[string]} mainAppsTab
	 * @param  {[string]} product
	 * @param  {[string]} pdpTab
	 * @returns {[string]}
	 */
	routeConstructor(path: string, mainAppsTab: string, product: string, pdpTab: string) {
		const mapObj = {
			mainAppsTab,
			product,
			pdpTab
		};

		const constructedPath = path.replace(/mainAppsTab|product|pdpTab/gi, matched => mapObj[matched]);

		return constructedPath;
	}

	isPhotoshopPostPurchase() {
		return Url.getParam('isStarter') && this.getCCWebPathData().product === 'photoshop';
	}

	isPostPurchasePhotoPlan() {
		return this.isPostPurchaseUser() && ((Url.getParam('plan') === 'ccpp' || Url.getParam('plan') === 'ccppn' || Url.getParam('plan') === 'lccp')
			|| (this.getPathData().mainAppsTab === starterTab && (this.getPathData().product === 'ccpp' || this.getPathData().product === 'ccppn' || this.getPathData().product === 'lccp')));
	}

	/**
	 * Compares two versions and returns true if versionA is greater than or equal to versionB, else returns false
	 *
	 * @param versionA Required param.
	 * @param versionB Required param.
	 * @returns true or false
	 */
	isAGreaterThanEqualToB(versionA, versionB, compareMinorVersion) {
		if (versionA === Infinity) {
			return true;
		}
		if (versionB === Infinity) {
			return false;
		}

		let compareResult;
		if (!compareMinorVersion) {
			let splitVersion = versionA.split('.');
			splitVersion.pop();
			const versionAWithoutMinor = splitVersion.join('.');

			splitVersion = versionB.split('.');
			splitVersion.pop();
			const versionBWithoutMinor = splitVersion.join('.');

			compareResult = window.cchome.util.compareVersionNumbers(versionAWithoutMinor, versionBWithoutMinor);
			return compareResult === 1 || compareResult === 0;
		}

		compareResult = window.cchome.util.compareVersionNumbers(versionA, versionB);
		return compareResult === 1 || compareResult === 0;
	}

	/**
	 * Returns the appropriate platform object extracted form the platform data (passed as the argument 'platform')
	 * based on the user's platform.
	 *
	 * @param platform Required param.
	 * @returns platform object
	 */
	getPlatformObject(platform) {
		let userPlatform;
		if (this.isMacOS()) {
			// Use whatever osx entry we have (because we can't determine architecture) and let
			// the CC App handle the error, if there is one.
			userPlatform = 'osx';
			if (!(platform.find(platform => (platform.id === userPlatform || platform.id.indexOf(userPlatform) >= 0)))) {
				userPlatform = 'mac';
			}
		}
		else if (this.isWin()) {
			// Use whatever win entry we have (because we can't determine architecture) and let the CC App handle the
			// error, if there is one.
			userPlatform = 'win';
		}
		return userPlatform ? platform.find(platform =>
			(platform.id === userPlatform || platform.id.indexOf(userPlatform) >= 0)
		) : platform[0];
	}

	/**
	 * Returns the appropriate languageSet object extracted form the platform object data (passed as the argument 'platformObject')
	 * based on the user's locale/language.
	 *
	 * @param platformObject Required param.
	 * @param userLocale Required param.
	 * @returns languageSet object
	 */
	getLanguageSet(platformObject, userLocale) {
		return platformObject.languageSet.find((langSet) => {
			const matchingLocale = Object.values(langSet.locales.locale).find(locale => (locale.name === userLocale || locale.name === 'mul'));
			return matchingLocale;
		});
	}

	/**
	 * Returns the product ID extracted form the CVS Endpoint.
	 *
	 * @param endpoint Required param.
	 * @returns product ID
	 */
	getProductIdFromCVSEndpoint(endpoint) {
		return endpoint.match(new RegExp('products/(.*)/pdp.model.json'))[1];
	}

	isAppNotEntitledForEnterpriseUser(sapcode) {
		const productConfig = this.getFFCProductConfigBySapCode(sapcode);
		return this.isUserEnterpriseUser() && !this.isAppEntitledNew(productConfig, productConfig.id, productConfig.type);
	}

	/**
	 * Returns true or false based on whether user is entitled to the given app (sap code passed in appCode) or not.
	 *
	 * @param appCode Required param.
	 * @param type Required param.
	 * @returns true or false
	 */
	isAppEntitled(appCode, type) {
		return this.isAppEntitledNew(this.appsFFCProductList[appCode],appCode, type);
	}

	/**
	 * Returns true or false based on whether user is entitled to the given app (sap code passed in appCode) or not.
	 *
	 * @param appCode Required param.
	 * @param type Required param.
	 * @returns true or false
	 */
	isAppEntitledNew(productObj,appCode, type) {
		if (this.isFFCFallback) {
			return true;
		}
		const entitlementData = this.entitlementData || {};
		const {profile = {}}: Object = this;
		const {productEntitlements, fulfilledEntitlements, subscriptionInfo = {}} = profile;
		// If the app is a desktop app, entitlement info is returned by FFC
		if (type.toLowerCase() === 'desktop' && productObj) {
			const product = productObj;
			let platformObject = this.getPlatformObject(product.platforms.platform);

			// If we can't determine the platform, use the first platform in the list.
			platformObject = platformObject ? platformObject : product.platforms.platform[0];

			if (platformObject) {
				const userLocale = FFCLocaleMap.en || 'en_US';
				// as per CCPD code, use URLs for en_US by default
				let languageSet = this.getLanguageSet(platformObject, userLocale);
				languageSet = languageSet ? languageSet : platformObject.languageSet[0];
				const locales = languageSet.locales.locale;
				const entitledLocales = locales.find(locale => locale.entitled === true);
				if (languageSet.nglLicensingInfo) {
					let entitledViaNGL = true;
					if (languageSet.nglLicensingInfo.status) {
						if (languageSet.nglLicensingInfo.status === 'LICENSE_ACTIVE' || languageSet.nglLicensingInfo.status === '') {
							entitledViaNGL = true;
						}
						else {
							entitledViaNGL = false;
						}
					}
					const customData = languageSet['custom-data'];
					if (customData && customData['custom-entry'].length > 0) {
						for (let i = 0; i < customData['custom-entry'].length; i++) {
							if (customData['custom-entry'][i].key === 'isFreeProduct') {
								if (customData['custom-entry'][i].value[0] === 'true') {
									entitledViaNGL = true;
								}
								break;
							}
						}
					}
					return entitledViaNGL;
				}
				return !!entitledLocales;
			}
		}

		else if (type === 'web') {
			// If the app is a web app, entitlement info is returned by JIL and entitlement service
			// An app appears entitled if any of the services shows this app entitled
			let isWebEntitled;
			if (subscriptionInfo.customerSegment === 'enterprise' && fulfilledEntitlements) {
				isWebEntitled = fulfilledEntitlements.includes(appCodeToJILWebAppName[appCode]);
			}
			else if (productEntitlements) {
				isWebEntitled = productEntitlements.includes(appCodeToJILWebAppName[appCode]);
			}
			if (isWebEntitled !== undefined) {
				return isWebEntitled;
			}
		}

		// If the app is a mobile app, entitlement info is returned by entitlement service
		else if (type === 'mobile' && entitlementData.allow_mobile !== undefined) {
			return entitlementData.allow_mobile;
		}

		// Default entitlement true for Desktop/Web/Mobile apps
		return true;
	}

	isAppFreemium(productObj, appCode, type) {
		let isFreemium = false;
		if (type.toLowerCase() === 'desktop' && productObj) {
			const product = productObj;
			let platformObject = this.getPlatformObject(product.platforms.platform);

			// If we can't determine the platform, use the first platform in the list.
			platformObject = platformObject ? platformObject : product.platforms.platform[0];

			if (platformObject) {
				const userLocale = FFCLocaleMap.en || 'en_US';
				// as per CCPD code, use URLs for en_US by default
				let languageSet = this.getLanguageSet(platformObject, userLocale);
				languageSet = languageSet ? languageSet : platformObject.languageSet[0];
				const nglLicensingInfo = languageSet.nglLicensingInfo;
				isFreemium = nglLicensingInfo && nglLicensingInfo.licenseMode && nglLicensingInfo.licenseMode.toLowerCase() === 'freemium'
						&& nglLicensingInfo.status && nglLicensingInfo.status.toLowerCase() === 'trial_not_available';
			}
		}
		return isFreemium;
	}

	/**
	 * Returns false if the given product is displayed.
	 *
	 * @param sapCode Required param.
	 * @returns true or false
	 */
	disableProduct(sapCode: string) {
		const disabledProducts = ['gemini', 'rocket', 'muse'];

		if (this.hasFeature('temp_cch_11689')) {
			disabledProducts.push('ffc-sprk', 'ffc_sppost', 'ffc_sppage', 'ffc_spvideo', 'ffc_rush');
		}
		else {
			disabledProducts.push('ffc_ccxpost', 'ffc_ccexpress', 'ffc_prexpr');
		}

		if (!this.hasFeature('temp_cch_11998')) {
			disabledProducts.push('psxuwp');
		}

		if (!this.hasFeature('temp_cch_8676')) {
			disabledProducts.push('stgr', 'ffc_sbstshare');
		}
		return disabledProducts.includes(sapCode.toLowerCase());
	}

	isSubstanceDesktopApp(sapCode: string) {
		return !this.hasFeature('temp_cch_8676') && (sapCode === 'SBSTP' || sapCode === 'SBSTA' || sapCode === 'SBSTD');
	}

	shouldShowPermissionReqdForSubstance(sapCode: string) {
		return this.isSubstanceDesktopApp(sapCode)
			&& !this.hasThor
			&& !this.isMobileOrTab();
	}

	/**
	 * Returns true or false based on whether the given product has to be displayed on the page or not.
	 *
	 * @param sapCode Required param.
	 * @param type Required param.
	 * @returns true or false
	 */
	displayTile(sapCode: string, type: string) {
		return ((type === 'desktop' && this.appsFFCProductList !== null && this.appsFFCProductList[sapCode])
			|| (type === 'web')
			|| (type === 'mobile'))
			&& !this.disableProduct(sapCode);
	}

	getCompanionApp(productid: string) {
		const mobileAppCode = companionMobileAppMap[productid];
		let store;
		if (DeviceDetectionUtil.isChromeOS() || DeviceDetectionUtil.isAndroid()) {
			store = 'android';
		}
		else if (DeviceDetectionUtil.isIPad()) {
			store = 'ipad';
		}
		else if (DeviceDetectionUtil.isIPhone()) {
			store = 'iphone';
		}
		if (mobileAppCode) {
			if (typeof mobileAppCode === 'string') {
				return mobileAppCode;
			}
			else if (typeof mobileAppCode === 'object' && store) {
				return mobileAppCode[store];
			}
			else if (!store) {
				return null;
			}
		}
		return mobileAppCode;
	}

	getCompanionAppBranchURL(productid: string) {
		if (this.isLoggedOutExp() && this.companionMobileAppData) {
			return this.companionMobileAppData.mobileAppUrl;
		}
		else if (!this.isLoggedOutExp()) {
			const sapCode = this.getCompanionApp(productid);
			if (sapCode) {
				const mobileAppConfig = this.getFFCProductConfigBySapCode(sapCode);
				if (mobileAppConfig) {
					const url = mobileAppConfig.mobileAppUrl;
					const connector = url.indexOf('?') > -1 ? '&' : '?';
					const branchURLTag = Url.getParam('plan') === 'all-apps'
						? `${connector}~tags=AllAppsPlan`
						: `${connector}~tags=PhotoshopPlan`;
					return `${url}${branchURLTag ? branchURLTag : branchURLTag}`;
				}
			}
		}
		return null;
	}

	/**
	 * Returns a Tile config object that constains all the information needed to display a tile. This is extracted from the json
	 * provided by CVS (passed as appConfig).
	 *
	 * @param product Required param.
	 * @returns Tile config
	 */
	getTileConfig(appConfig: Object) {
		// const appConfig = this.productsData[product];
		if (appConfig.productId) {
			const sapCode = appConfig.productId;
			const pdpURL = appConfig.productDetailEndpoint && appConfig.productDetailEndpoint
				.replace('https://', '')
				.replace('http://', '');
			const displayName = appConfig.title;
			const desc = appConfig.description;
			const mainImage = appConfig.bannerBgImage ? appConfig.bannerBgImage.src : '';
			const productIcon = appConfig.bannerIcon ? appConfig.bannerIcon.src : '';
			const type = appConfig.platform;
			const entitled = this.isAppEntitled(appConfig.productId, type);
			const productId = this.getProductIdFromCVSEndpoint(appConfig.productDetailEndpoint);
			let model;
			switch (type) {
				case 'desktop':
					model = 'desktop';
					break;
				case 'mobile':
					model = 'mobile';
					break;
				case 'web':
					model = 'webApp';
					break;
			}
			if (model !== 'mobile') {
				switch (entitled) {
					case true:
						model += 'InPlan';
						break;
					case false:
						model += 'NotInPlan';
						break;
				}
			}
			const buttonConfig = appConfig.ctaContainer.targetedModels[model];
			const platformIcon = appConfig.platformIcon;
			const planIcon = appConfig.planIcon;
			const learnMoreLabel = appConfig.learnMoreLabel;
			const version = this.appsFFCProductList[sapCode] ? this.appsFFCProductList[sapCode].version : null;

			return {displayName, productIcon, sapCode, mainImage, desc, pdpURL, entitled, buttonConfig, platformIcon, planIcon, productId, type, learnMoreLabel, version};
		}
		// case when we pass ffc object as config
		const sapCode = appConfig.id;
		const displayName = appConfig.displayName;
		const desc = this.getProductTagLine(appConfig, this.locale);
		const type = appConfig.type.toLowerCase();
		const entitled = this.isAppEntitledNew(appConfig, appConfig.id, appConfig.type);
		// const productId = this.getProductIdFromCVSEndpoint(appConfig.productDetailEndpoint);
		let model = '';
		switch (type) {
			case 'desktop':
				model = 'desktop';
				break;
			case 'mobile':
				model = 'mobile';
				break;
			case 'web':
				model = 'webApp';
				break;
		}
		if (model !== 'mobile') {
			switch (entitled) {
				case true:
					model += 'InPlan';
					break;
				case false:
					model += 'NotInPlan';
					break;
			}
		}
		const version = appConfig[sapCode] ? appConfig[sapCode].version : null;

		return {displayName, sapCode, desc, entitled, type, version};
	}

	/**
	 * Converts AEM class name to CCH class name
	 *
	 * @param className Required param.
	 * @returns CCH Class name
	 */
	convertAEMToCCHClass(className: string) {
		return className.replace(/aem/g, 'cch');
	}

	/**
	 * returns true if device is touch based
	 *
	 * @returns true or false
	 */
	isTouchDevice() {
		return DeviceDetectionUtil.isTouchDevice();
	}

	/**
	 * returns true if device is ARM based
	 *
	 * @returns true or false
	 */
	isARM() {
		return DeviceDetectionUtil.isARM();
	}

	/**
	 * returns true if device is Chrome OS based. Eg: a chromebook
	 *
	 * @returns true or false
	 */
	isChromeOS() {
		return DeviceDetectionUtil.isChromeOS();
	}

	isWin() {
		return DeviceDetectionUtil.isWin();
	}

	isMacOS() {
		return DeviceDetectionUtil.isMacOS();
	}

	getWebAppLaunchUrl(sapcode: ?string) {
		if (sapcode && this.appsFFCServiceList[sapcode]) {
			return this.appsFFCServiceList[sapcode].serviceUrl;
		}
	}

	isSafari() {
		return DeviceDetectionUtil.isSafari();
	}

	isFirefox() {
		if (platform && platform.name) {
			return platform.name.toLowerCase().includes('firefox');
		}
		return false;
	}

	isEdgeOrIE() {
		return DeviceDetectionUtil.isEdgeOrIE();
	}

	isChrome() {
		return DeviceDetectionUtil.isChrome();
	}

	isIPad() {
		return DeviceDetectionUtil.isIPad();
	}

	isIPhone() {
		return platform && platform.os && platform.os.family && platform.os.family.toLowerCase() === 'ios'
			&& platform.product && platform.product.toLowerCase() === 'iphone';
	}

	isAndroid() {
		return platform && platform.os && platform.os.family && platform.os.family.toLowerCase() === 'android';
	}

	isWindowsPhone() {
		return platform && platform.os && platform.os.family && platform.os.family.toLowerCase() === 'windows phone';
	}

	isCCDSupportedOS() {
		return !DeviceDetectionUtil.isLinux();
	}

	/**
	 * Capiatalze the first letter of string.
	 * @param string
	 * @returns {string}
	 */
	capitalizeFirstLetter(string: string) {
		return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
	}

	isMobileBreakPoint() {
		return window.matchMedia && !window.matchMedia('(min-width: 768px)').matches;
	}

	isMobileOrTab() {
		return DeviceDetectionUtil.isMobileOrTab();
	}

	goURL(base) {
		let ret = `${base}_${getConfigApps('env')}`;
		if (this.locale === 'zh-Hans') {
			ret = `${ret}_cn`;
		}
		else if (this.locale === 'zh-Hant') {
			ret = `${ret}_tw`;
		}
		else if (this.locale !== 'en' && this.locale !== 'en_US') {
			ret = `${ret}_${this.locale}`;
		}
		return ret;
	}

	hasFeature(feature: string) {
		return window.cchome && window.cchome.util && window.cchome.util.hasFeature && window.cchome.util.hasFeature(feature);
	}

	isUserEnterpriseUser() { // validation is behind the feature flag
		if (!this.customerSegment) {
			return false;
		}
		return this.customerSegment.toUpperCase() === customerSegmentValues.ENTERPRISE || this.customerSegment.toUpperCase() === customerSegmentValues.TEAM;
	}

	isUserFree() {
		return this.profile && this.profile.subscriptionInfo && this.profile.subscriptionInfo.planCode === 'unknown';
	}

	// Returns custom title Text for the document as per the active content in Apps section
	getDocumentTitle(pathname, intl) {
		let documentTitle = '';
		const pathId = pathname.split('/')[2];

		// If passed path URL is found in Map, use it to identify the Title String
		// Else default back to top-level Title string for Apps page
		if (this.getCCWebPathData().isPDP) {
			const ffcProdConfig = this.getFFCProductConfig(this.getCCWebPathData().product);
			documentTitle = ffcProdConfig ? ffcProdConfig.displayName : intl.formatMessage({id: 'apps.pdp.apps'});
		}
		else if (pathId && pathId in pathTitleMap) {
			documentTitle = intl.formatMessage({id: pathTitleMap[pathId]});
		}
		else {
			documentTitle = intl.formatMessage({id: 'apps.pdp.apps'});
		}
		// Before returning, append the following string - " | Adobe Creative Cloud"
		return documentTitle + intl.formatMessage({id: 'apps.document.title.suffix'});
	}

	isVersionEnabled(versionObj) {
		let customEntry = false;
		try {
			customEntry = versionObj.platforms.platform[0]['custom-data']['custom-entry'];
		}
		catch (e) {}
		if (customEntry) {
			for (let k = 0; k < customEntry.length; ++k) {
				const customEntryObj = customEntry[k];
				if ((customEntryObj.key === 'isVersionDisabled') && (customEntryObj.value && customEntryObj.value[0] && typeof customEntryObj.value[0] === 'string' && customEntryObj.value[0].toLowerCase() === 'true')) {
					return false;
					break;
				}
			}
		}
		return true;
	}

	getFFCProductSapcodesSet(data: Object) {
		const sapcodeSet: Set<mixed> = new Set();
		if (data && typeof data.channel != 'undefined') {
			const ccmChannel = data.channel.find(channel => channel.name === 'ccm');
			const productArr = ccmChannel ? ccmChannel.products.product : [];
			productArr.forEach(obj => sapcodeSet.add(obj.id));
		}
		return sapcodeSet;
	}

	filterFallbackFFCData(data: ?Object, sapCode) {
		let retData = null;
		if (data && typeof data.channel != 'undefined') {
			const ccmChannel = data.channel.find(channel => channel.name === 'ccm');
			const productArr = ccmChannel ? ccmChannel.products.product : [];
			const filteredProductArr = productArr.filter(obj => obj.id === sapCode);
			retData = {
				channel: [{
					name: ccmChannel.name,
					products: {
						product: filteredProductArr
					}
				}]
			};
		}
		return retData;
	}

	processAndStoreProductFFCData(data, sapCode) {
		if (data && typeof data.channel != 'undefined') {
			const ccmChannel = data.channel.find(channel => channel.name === 'ccm');
			this.allVersionsFFCProductList[sapCode] = ccmChannel ? ccmChannel.products.product : undefined;
			this.allVersionsFFCProductList[sapCode] = this.allVersionsFFCProductList[sapCode].filter(this.isVersionEnabled);
			this.allVersionsFFCProductList[sapCode] = this.allVersionsFFCProductList[sapCode].sort((a, b) => window.cchome.util.compareVersionNumbers(a.version, b.version));
		}
	}

	/**
	 * This method checks to see if cookies are enabled.
	 * @param {string} type the type of storage to check for.
	 */
	isCookieEnabled() {
		try {
			let cookieEnabled = navigator.cookieEnabled;
			if (!cookieEnabled) {
				document.cookie = 'testcookie';
				cookieEnabled = document.cookie.indexOf('testcookie') !== -1;
			}
			return cookieEnabled;
		}
		catch (e) {
			return false;
		}
	}

	/**
	 *
	 * @param logData
	 */
	printLog(logData) {
		const printLogFlag = this.hasFeature('temp_enable_cchome_apps_logs'); // don't enable the flag on production. It should only be used for debugging.
		if (printLogFlag) {
			console.log(`Apps Section logging - ${logData}`);
		}
	}

	printTimeBeginLog(label) {
		const printLogFlag = this.hasFeature('temp_enable_cchome_apps_logs'); // don't enable the flag on production. It should only be used for debugging.
		if (printLogFlag) {
			console.time(label);
		}
	}

	printTimeEndLog(label) {
		const printLogFlag = this.hasFeature('temp_enable_cchome_apps_logs'); // don't enable the flag on production. It should only be used for debugging.
		if (printLogFlag) {
			console.timeEnd(label);
		}
	}

	isPostPurchaseUser() {
		return Url.getParam('isStarter') || this.getPathData().mainAppsTab === starterTab;
	}

	isPostPurchaseUserForCCPPOrCCPPN() {
		const routeParams = this.getPathData();
		return this.isPostPurchaseUser() && (Url.getParam('plan') === 'ccpp' || Url.getParam('plan') === 'ccppn')
			|| (routeParams.mainAppsTab === 'starter' && (routeParams.product === 'ccpp' || routeParams.product === 'ccppn'));
	}

	isPostPurchaseUserForPhotoshop() {
		const routeParams = this.getPathData();
		const routeParamsCCW = this.getCCWebPathData();
		return this.isPostPurchaseUser() && (routeParams.product === 'photoshop' || routeParamsCCW.product === 'photoshop') && (Url.getParam('plan') !== null && Url.getParam('plan').toLowerCase() === 'photoshop');
	}

	/* Returns the plan if this is a single app post purchase url, else returns null */
	getSingleAppPostPurchasePlan() {
		const routeParams = this.getPathData();
		const routeParamsCCW = this.getCCWebPathData();
		const isAfterRedirectStarterUrl = Url.getParam('isStarter') === 'true';
		const planParam = Url.getParam('plan');
		let productId = singleAppPlans.includes(routeParams.product) ? routeParams.product : routeParamsCCW.product;
		if ((productId === 'creative-cloud') && isAfterRedirectStarterUrl && Url.getParam('plan')) {
			// This is for the scenario when someone clicks on 'Install or open CCD' on incompatible OS and get redirect to CCD page
			productId = Url.getParam('plan');
		}
		if (this.isPostPurchaseUser()
			&& singleAppPlans.includes(productId)
			&& (!isAfterRedirectStarterUrl || (planParam && singleAppPlans.includes(planParam.toLowerCase())))) {
			return productId;
		}
		return null;
	}

	isNujM3JdiSingleApp(planId) {
		return nujM3SingleAppPlansJDI.includes(planId);
	}

	/* Returns TWP (Trial with payment) users */
	isTrialUser() {
		const profile = this.profile;
		const promotionType = profile && profile.subscriptionInfo && profile.subscriptionInfo.promotionType;
		if (promotionType) {
			return promotionType.toLowerCase() === 'trialm';
		}
		return false;
	}

	isUserEligibleForThroughline() {
		const countryCode = window.adobeIMS && window.adobeIMS.getUserProfile && window.adobeIMS.getUserProfile() && window.adobeIMS.getUserProfile().countryCode;

		if (this.hasFeature('temp_enable_throughline_workflow')) {
			return (countryCode && (countryCode.toLowerCase() === 'us' || countryCode.toLowerCase() === 'ca'))
				&& !this.hasThor
				&& this.locale === 'en'
				&& this.isIndividualUser()
				&& this.isPostPurchaseUserForCCPPOrCCPPN()
				&& this.isTrialUser();
		}
		return false;
	}

	isUserEligibleForCategoryRevamp() {
		return !this.isUserEnterpriseUser()
			&& (!this.locale || this.locale === 'en')
			&& !this.isLoggedOutExp()
			&& (DeviceDetectionUtil.isMacOS()
			|| DeviceDetectionUtil.isWin())
			&& !DeviceDetectionUtil.isMobileOrTab();
	}

	isDownloadPage(product: string) {
		const pathDataCCWeb = this.getCCWebPathData();

		return (pathDataCCWeb.isPDP && pathDataCCWeb.product === product || Url.getParam('productId') === product)
			&& Url.getParam('action') === 'install';
	}

	isUserEligibleForThroughlineV3EnLocale() {
		const countryCode = window.adobeIMS && window.adobeIMS.getUserProfile && window.adobeIMS.getUserProfile() && window.adobeIMS.getUserProfile().countryCode;

		return (countryCode && (countryCode.toLowerCase() !== 'us' && countryCode.toLowerCase() !== 'ca'))
			&& !this.hasThor
			&& (this.locale === 'en')
			&& this.isIndividualUser()
			&& (this.isPostPurchaseUserForPhotoshop() || (this.isDownloadPage('photoshop') && this.planCode === 'phsp_direct_individual' && this.isAppEntitled('phsp', 'desktop')))
			&& this.isTrialUser();
	}

	isUserEligibleForThroughlineV3JaLocale() {
		return !this.hasThor
			&& this.locale === 'ja'
			&& this.isIndividualUser()
			&& (this.isPostPurchaseUserForPhotoshop()
				|| this.isPostPurchaseUserForCCPPOrCCPPN()
				|| (
					this.isDownloadPage('photoshop')
					&& (this.planCode === 'phsp_direct_individual' || this.isCCPPPlanCode(this.planCode))
					&& this.isAppEntitled('phsp', 'desktop')
				)
				|| (
					this.isDownloadPage('lightroom')
					&& this.isCCPPPlanCode(this.planCode)
					&& this.isAppEntitled('lrcc', 'desktop')
				)
			);
	}

	fetchURLFromSophiaData(sophiaRes, surfaceIdArray) {
		const endPointsObject = {};
		if (sophiaRes && sophiaRes.data) {
			surfaceIdArray.forEach((surfaceId) => {
				endPointsObject[surfaceId] = sophiaRes.data.surfaces[surfaceId] && sophiaRes.data.surfaces[surfaceId].containers.filter(container => container.dataType === 'URL')[0].data;
			});
		}
		return endPointsObject;
	}

	// cohort for Throughline - SinglePs
	isUserEligibleForThroughlineSinglePs() {
		const countryArray = [
			'at', 'be', 'bg', 'cy', 'cz', 'dk', 'ee', 'fi', 'fr', 'de', 'gr', 'hu', 'ie', 'it', 'lv',
			'lt', 'lu', 'mt', 'nl', 'no', 'pl', 'pt', 'ro', 'sk', 'si', 'es', 'se', 'ch', 'gb'
		];
		const countryCode = window.adobeIMS && window.adobeIMS.getUserProfile && window.adobeIMS.getUserProfile() && window.adobeIMS.getUserProfile().countryCode;

		return ((countryCode && (countryArray.indexOf(countryCode.toLowerCase()) > -1)) || this.hasFeature('temp_extend_throughline_nonTWP_cohort'))
			&& !this.hasThor
			&& this.locale === 'en'
			&& this.isIndividualUser()
			&& this.isPostPurchaseUserForPhotoshop()
			&& !this.isTrialUser();
	}

	isIndividualUser() {
		const customerSegment = this.profile && this.profile.subscriptionInfo && this.profile.subscriptionInfo.customerSegment;
		if (customerSegment) {
			return customerSegment.toLowerCase() === 'individual' || customerSegment.toLowerCase() === 'unknown';
		}
		return false;
	}

	setTabIndex(elem, tabIndex) {
		if (elem) {
			elem.tabIndex = tabIndex;
		}
	}

	isWin10() {
		const agent = navigator.userAgent.toLowerCase();
		const win10 = agent.match(('Windows NT 10.0').toLowerCase());
		return (win10 && win10.length > 0);
	}

	getSystemReqURL(productId) {
		return `http://www.adobe.com/go/system_req_page_${productId}_${getConfigApps('env')}`;
	}

	processAndStoreFFCFallbackProducts(products) {
		const productsObj = {};
		products.data.productlist.forEach((prod) => {
			productsObj[prod.sapCode] = prod;
		});
		this.ffcFallbackProductsJson = productsObj;
	}

	getContextGuid(sapCode: string, hasThor) {
		const isCompatibleOS = (sapCode === 'KCCC') || CompatibilityUtil.isAppSupported(sapCode);
		const outcome = isCompatibleOS || CompatibilityUtil.isAnyOlderVersionSupported(sapCode);
		const entry = this.isPostPurchaseUser() ? 'post-purchase' : 'cch';
		let guid = isCompatibleOS ? 'os-iscompatible' : 'os-incompatible';
		guid += `/entry=${entry}/thor=${hasThor}/outcome=${String(outcome)}`;
		return guid;
	}

	addParamsToStockUrl(url: string = '', src) {
		const source = stockUrlParams[src];
		if (url.indexOf('{app-name}') !== -1
			|| url.indexOf('{app-version}') !== -1
			|| url.indexOf('{campaign}') !== -1
			|| url.indexOf('{click-source}') !== -1
			|| source === stockUrlParams.banner) {
			let newUrl = url.replace('{app-name}', 'CCHome');
			newUrl = newUrl.replace('{app-version}', 'CCHome');
			newUrl = newUrl.replace('{click-source}', source);
			newUrl = newUrl.replace('{campaign}', 'apps');
			// CCH-11570 fixing as_source to be CCHome
			if ((Url.getParam('as_source',url)) !== 'CCHome') {
				newUrl = Url.replaceParamInUrl('as_source','CCHome',newUrl);
			}
			if ((Url.getParam('as_channel',url)) !== 'adobe_com') {
				newUrl = Url.replaceParamInUrl('as_channel','adobe_com',newUrl);
			}
			return newUrl;
		}
		let newUrl = url;
		if (newUrl.indexOf('as_channel') === -1) {
			newUrl = `${newUrl}&as_channel=adobe_com`;
		}
		else {newUrl = Url.replaceParamInUrl('as_channel','adobe_com',newUrl);}

		if (newUrl.indexOf('as_source') === -1) {
			newUrl = `${newUrl}&as_source=CCHome`;
		}
		else {newUrl = Url.replaceParamInUrl('as_source', 'CCHome', newUrl);}

		if (newUrl.indexOf('as_campclass') === -1) {
			newUrl = `${newUrl}&as_campclass=brand`;
		}
		else {newUrl = Url.replaceParamInUrl('as_campclass', 'brand', newUrl);}

		if (newUrl.indexOf('as_campaign') === -1) {
			newUrl = `${newUrl}&as_campaign=apps`;
		}
		else {newUrl = Url.replaceParamInUrl('as_campaign', 'apps', newUrl);}

		if (newUrl.indexOf('as_audience') === -1) {
			newUrl = `${newUrl}&as_audience=users`;
		}
		else {newUrl = Url.replaceParamInUrl('as_audience', 'users', newUrl);}

		return `${newUrl}&as_content=${source}`;
	}

	isCCHInLMS() {
		const lmsValue = Url.getParam('lms');
		if (typeof window.isCCHInLMSCanvas === 'undefined') {
			window.isCCHInLMSCanvas = lmsValue !== null && lmsValue.toLowerCase() === 'canvas';
		}
		if (typeof window.isCCHInLMSBlackboard === 'undefined') {
			window.isCCHInLMSBlackboard = lmsValue !== null && lmsValue.toLowerCase() === 'blackboard';
		}
		return window.isCCHInLMSCanvas || window.isCCHInLMSBlackboard;
	}

	updateAnalyticsUrlForCanvas() {
		let analyticsUrl = document.location.href;
		const queryStrings = ['lms=canvas','lms=blackboard'];
		queryStrings.forEach((queryString) => {
			if (analyticsUrl) {
				const indexOfCanvasParam = analyticsUrl.indexOf(`?${queryString}`);
				if (indexOfCanvasParam !== -1) {
					if (analyticsUrl.length === indexOfCanvasParam + `?${queryString}`.length) {
						analyticsUrl = analyticsUrl.replace(`?${queryString}`, '');
					}
					else {
						analyticsUrl = analyticsUrl.replace(`?${queryString}&`, '?');
						analyticsUrl = analyticsUrl.replace(`?${queryString}#`, '');
					}
				}
				else if (analyticsUrl.indexOf(`&${queryString}`) !== -1) {
					analyticsUrl = analyticsUrl.replace(`&${queryString}#`, '');
					analyticsUrl = analyticsUrl.replace(`&${queryString}`, '');
				}
			}
		});
		return analyticsUrl;
	}

	getCVSDomain() {
		const servers = getConfigApps('servers');
		return this.hasFeature('temp_use_cvs_prod') ? servers.CVSProd : servers.CVS;
	}

	getAEMDomain() {
		const servers = getConfigApps('servers');
		return this.hasFeature('temp_use_aem_prod') ? servers.AEMPROD : servers.AEM;
	}

	jarvisInitCallback = () => {
		this.jarvisPromiseResolve(true);
	}

	addJarvisEventCallback(event: string, callback: Function) {
		this.jarvisInitiated.then(() => document.getElementById('adbmsgCta').addEventListener(event, callback));
	}

	removeJarvisEventCallback(event: string, callback: Function) {
		this.jarvisInitiated.then(() => document.getElementById('adbmsgCta').removeEventListener(event, callback));
	}

	stringConcatWithPipe = (parString: string = '', curString) => {
		let retString = parString;
		if (retString !== '') {
			retString += `|${curString}`;
		}
		else {
			retString = curString;
		}
		return retString;
	}

	getInstallationModalUrl(mainConfig, options: Object = {}) {
		const {sapCode, source, plan, platform = null} = mainConfig;
		const productIdQueryParam = `productId=${this.getProductIdFromSapcode(sapCode)}`;
		const planQueryParam = plan ? `&plan=${plan}` : '';
		if (options.isCategoryPath) {
			const starterParam = options.isStarter ? '&isStarter=true' : '';
			return `/apps/${options.category}/${options.subCategory}?action=install${starterParam}&${productIdQueryParam}${planQueryParam}`;
		}
		const url = this.getCCWebURL(null, platform, true, false, source);
		return `${url}${url.includes('?') ? '&' : '?'}${productIdQueryParam}${planQueryParam}`;
	}

	/* Deep linking of Urls based on sapcode and action
	 * CCH-7488
	 * Url pattern ?sapcode={sapcode}, ?sapcode={sapcode}&action=pdp/install/update/open
	 *
	 */

	getProductUrl(sapCode: ?string, action: ?string) {
		if (!sapCode) {
			return;
		}
		const sapCodeInUpperCase = sapCode.toUpperCase();
		if (!sapCodeToProductMetaData[sapCodeInUpperCase]) {
			return;
		}
		const platform = sapCodeToProductMetaData[sapCodeInUpperCase].platform;
		const productId = sapCodeToProductMetaData[sapCodeInUpperCase].productId;
		if (!productId || !platform) {
			return;
		}
		if (!action || action === 'pdp' || action !== 'install') {
			return `/apps/all/${platform}/pdp/${productId}`;
		}
		return `/apps/all/${platform}?action=install&productId=${productId}`;
	}

	getProductIdFromSapcode(sapCode: ?string) {
		if (!sapCode) {
			return '';
		}
		const sapCodeInUpperCase = sapCode.toUpperCase();
		if (!sapCodeToProductMetaData[sapCodeInUpperCase]) {
			return '';
		}
		return sapCodeToProductMetaData[sapCodeInUpperCase].productId;
	}

	isValidCCHomeProduct(productId: string) {
		return Object.keys(productIdToSapCode).indexOf(productId) > -1;
	}

	convertHyphenatedToCamelCase(inputString: string) {
		return inputString && inputString.replace(/-([a-z])/g, charAfterHyphen => charAfterHyphen[1].toUpperCase());
	}

	getShortUrlForPagename(url = window.location.href) {
		if (!url) {return '';};
		const isStarter = Url.getParam('isStarter', url);
		const action = Url.getParam('action', url);
		const trimmedUrlParts = url.split(/[?#]/);
		if (!trimmedUrlParts.length || !trimmedUrlParts[0]) {
			return '';
		}
		let trimmedUrl = trimmedUrlParts[0];
		
		if (isStarter) {
			trimmedUrl = `${trimmedUrl}?isStarter=${isStarter}`;
		}
		if (action) {
			const prefix = isStarter ? '&' : '?';
			trimmedUrl = `${trimmedUrl}${prefix}action=${action}`;
		}
		return trimmedUrl;
	}

	getEventPageNameField() {
		const referrer = this.getShortReferrer();
		const url = this.getShortUrlForPagename();
		return `${url}${referrer ? `|${referrer}` : ''}`;
	}

	getShortReferrer() {
		let referrer = window.document.referrer || '';
		const storedReferrer = this.sessionStorageGet('creative-cloud-referrer');
		if (storedReferrer) {
			try {
				const referrerDetails = JSON.parse(storedReferrer);
				referrer = referrerDetails.referrer;
			}
			catch (e) {}
		}

		return this.getShortUrlForPagename(referrer || '');
	}
}

export default new AppsUtil();
